import { Area, Getter, Setter, Walls } from '@types'
import { toVector3 } from '@utils/conversion/toVector3'
import { getBaseArea } from '@utils/sceneObjects/areas/getBaseArea'
import { closeFloorAndRoof } from '@utils/sceneObjects/areas/hydrateClosedRoomAreas'
import isArea from '@utils/sceneObjects/assertions/isArea'
import isLight from '@utils/sceneObjects/assertions/isLight'
import isWall from '@utils/sceneObjects/assertions/isWall'
import getId from '@utils/sceneObjects/getId'
import { getBaseWall } from '@utils/sceneObjects/walls/getBaseWall'
import { getInnerAngle } from '@utils/sceneObjects/walls/getInnerAngle'
import getRelevantPoints from '@utils/sceneObjects/walls/getRelevantPoints'
import { Matrix4 } from 'three'

export const createAreaActions = (set: Setter, get: Getter) => ({
  mergeWalls: (lineB: string, lineBRotation?: number) => {
    set((store) => {
      const B = store.items.get(lineB)! as Walls

      const A = store.items.get(B?.prevWall)! as Walls
      if (!A || !B) {
        if (!A && !B) {
          console.error('Error on merging lines. A and B not found', { lineB, A })
          return
        }
        if (!A) {
          console.error('Error on merging lines. A line not found', { lineB })
          return
        }
        if (!B) {
          console.error('Error on merging lines. B line not found', { lineB })
          return
        }
      }
      const AThickness = A.thickness
      const BThickness = B.thickness
      const AAngle = A.rotation
      const BAngle = lineBRotation ?? B.rotation

      const {
        beta,
        absoluteCross,
        absoluteDot,
        symmetricalPerpendicularPoint,
        perpendicularPoint,
        dotAB,
        cross,
      } = getRelevantPoints({
        AStart: A.start,
        AEnd: A.end,
        BStart: B.start,
        BEnd: B.end,
        AAngle,
        AThickness,
        BAngle,
        BThickness,
      })
      A.beta = beta
      A.absoluteCross = absoluteCross
      A.absoluteDot = absoluteDot
      A.symmetricalPerpendicularPoint = symmetricalPerpendicularPoint
      A.perpendicularPoint = perpendicularPoint
      A.dot = dotAB
      A.cross = cross
      B.prevDot = dotAB
      B.prevCross = cross
    })
  },
  addToArea: (lineId: string) => {
    set((store) => {
      const area = store.areas.get(store.currentArea)
      if (!area) return /* console.warn(`Area not found while adding to area the line: ${lineId}`) */
      area.sides.push(lineId)
    })
  },
  editAreaTexture: <T extends Area>(id: string, config?: Partial<T>) => {
    if (config) {
      set((store) => {
        for (const [property, value] of Object.entries(config)) {
          const area = store.areas.get(id)
          if (!area) continue

          area.textures.forEach((prop, propIndex) => {
            if (property === prop.id) {
              // Sobreescribo las llaves del objeto por aquellas incluidas en el config e incluyo sin tocar las llaves no modificadas
              const prevData = area.textures[propIndex]
              area.textures[propIndex] = { ...prevData, ...value }
            }
          })
        }
      })
    }
  },
  createArea: (areaId: string, wallID: string, x: number, z: number) => {
    set((store) => {
      const roofID = getId('areas')
      store.items.set(
        wallID,
        getBaseWall(
          {
            id: wallID,
            x: x,
            z: z,
            start: [x, z],
            areas: new Set([areaId, roofID]),
            end: [x + 1, z + 1],
          },
          store,
        ),
      )
      store.layers[store.currentLayer].selected.add(wallID)
      store.layers[store.currentLayer].items.add(wallID)
      store.layers[store.currentLayer].areas.add(areaId)
      store.currentArea = areaId
      store.currentRoof = roofID

      store.areas.set(
        areaId,
        getBaseArea({
          id: areaId,
          sides: [wallID],
          isClosed: false,
        }),
      )
      store.areas.set(
        roofID,
        getBaseArea({
          id: roofID,
          sides: [wallID],
          isClosed: true,
          isRoof: true,
        }),
      )
    })
  },
  closeArea: () => {
    const drawLight = get().drawLight
    set((store) => {
      const area = store.areas.get(store.currentArea)
      const roof = store.areas.get(store.currentRoof)
      if (!area || !roof) return /* console.warn(`Error when closing area ${store.currentArea}`) */
      const wall = store.items.get(store.currentObjectID)
      if (isWall(wall)) {
        // Add beta between line and firstLine
        wall.nextWall = store.currentFirstWall
        const firstLine = store.items.get(store.currentFirstWall)
        if (isWall(firstLine)) {
          firstLine.prevWall = wall.id
        }
      }
      let height = -Infinity
      const newSides: string[] = []
      let maxX = -Infinity
      let maxZ = -Infinity
      let minX = Infinity
      let minZ = Infinity
      const futureLines: [Walls, Walls] = []
      area.sides.forEach((lineID, i, l) => {
        newSides.push(lineID)
        const line = store.items.get(lineID)
        if (i === 0 || i === l.length - 1) {
          futureLines.push(line)
        }
        if (!isWall(line)) return
        if (line.isFirst) {
          line.isFirst = false
        }
        if (line.x > maxX) {
          maxX = line.x
        }
        if (line.z > maxZ) {
          maxZ = line.z
        }
        if (line.x < minX) {
          minX = line.x
        }
        if (line.z < minZ) {
          minZ = line.z
        }
        if (line.height > height) {
          height = line.height
        }
      })

      const [firstLine, lastLine] = futureLines
      const { innerAngle } = getInnerAngle(
        lastLine.start,
        lastLine.end,
        firstLine.start,
        firstLine.end,
      )
      firstLine.innerAngle = innerAngle
      store.cameraCenter = [(maxX + minX) / 2, height, (maxZ + minZ) / 2]
      const lightMatrix = new Matrix4()
        .clone()
        .setPosition(toVector3([(maxX + minX) / 2, height + 100, (maxZ + minZ) / 2]))

      const _id = 'lights_baseSceneLight'
      const _id2 = 'lights_baseAreaLight'
      if (
        !store.items.has('lights_baseSceneLight') ||
        !store.layers[store.currentLayer].items.has('lights_baseSceneLight')
      ) {
        drawLight(_id, '', 'LUZGENERAL', false)
      } else {
        const base = store.items.get('lights_baseSceneLight')
        if (isLight(base)) {
          base.matrix = lightMatrix
        }
      }
      drawLight(_id, '', 'LUZGENERAL', false)
      // const lightMatrix2 = new Matrix4()
      //   .clone()
      //   .setPosition(toVector3([(maxX + minX) / 2 + 20, height + 100, (maxZ + minZ) / 2 + 20]))
      if (
        !store.items.has('lights_baseAreaLight') ||
        !store.layers[store.currentLayer].items.has('lights_baseAreaLight')
      ) {
        drawLight(_id2, '', 'Luzgeneral2', false)
      } else {
        const base = store.items.get('lights_baseAreaLight')
        if (isLight(base)) {
          // base.matrix = lightMatrix2
        }
      }
      // }

      roof.sides = newSides
      roof.height = height
      store.areas.set(
        roof.id,
        getBaseArea({
          sides: newSides,
          isRoof: true,
          height,
          id: roof.id,
          textures: roof.textures,
          isClosed: true,
        }),
      )
      area.isClosed = true
      roof.isClosed = true
      closeFloorAndRoof(store)
    })
  },
  updateArea: (lineID: string) => {
    set((store) => {
      const line = store.items.get(lineID)

      // **Si la línea no es una pared, no hacemos nada**
      if (!isWall(line)) return

      line?.areas?.forEach((areaID) => {
        const area = store.areas.get(areaID)

        //  **Si `areaID` es una pared por error, lo corregimos**
        if (!area || !isArea(area) || !area.isRoof || !area.isClosed) return

        area.sides.forEach((sideID) => {
          const sideLine = store.items.get(sideID)
          if (!isWall(sideLine)) return
          if (sideLine.height > area.height) area.height = sideLine.height
        })
      })
    })
  },
  editAreaHeight: (areaID: string, newHeight: number) => {
    set((store) => {
      const area = store.areas.get(areaID)
      if (!area || !isArea(area)) return

      const mode = store.mode

      // En MODE_FORCE_ALZADOS, restringir el height entre 0 y la altura máxima de las walls
      if (mode === 'MODE_FORCE_ALZADOS') {
        let maxWallHeight = 0

        // Calcular la altura máxima de las walls que forman los lados del área
        area.sides.forEach((sideID) => {
          const sideLine = store.items.get(sideID)
          if (isWall(sideLine) && sideLine.height > maxWallHeight) {
            maxWallHeight = sideLine.height
          }
        })

        // Restringir el height entre 0 y maxWallHeight
        newHeight = Math.max(0, Math.min(newHeight, maxWallHeight))
      }

      area.height = newHeight
    })
  },
  incrementAreaHeight: (areaID: string, deltaY: number) => {
    set((store) => {
      const area = store.areas.get(areaID)
      if (!area || !isArea(area)) return

      const mode = store.mode
      const currentHeight = area.height || 0
      let newHeight = currentHeight + deltaY

      // En MODE_FORCE_ALZADOS, restringir el height entre 0 y la altura máxima de las walls
      if (mode === 'MODE_FORCE_ALZADOS') {
        let maxWallHeight = 0

        // Calcular la altura máxima de las walls que forman los lados del área
        area.sides.forEach((sideID) => {
          const sideLine = store.items.get(sideID)
          if (isWall(sideLine) && sideLine.height > maxWallHeight) {
            maxWallHeight = sideLine.height
          }
        })

        // Restringir el height entre 0 y maxWallHeight
        newHeight = Math.max(0, Math.min(newHeight, maxWallHeight))
      }

      area.height = newHeight
    })
  },
  editAreaProperty: <T extends Area>(id: string, config?: Partial<Record<keyof T, T[keyof T]>>) => {
    if (config) {
      set((store) => {
        const area = store.areas.get(id)
        if (!area || !isArea(area)) return
        for (const [property, value] of Object.entries(config)) {
          area[property] = value
        }
      })
    }
  },
})
